import { Box, Divider, Icon, IconButton, Paragraph, Text } from '@zillow/constellation-10';
import { IconChevronRightFilled, IconUserFilled } from '@zillow/constellation-icons';
import { ChatAvatar, chatBubbleStyles, defaultChatBubbleStyles } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import { useContext } from 'react';
import { useRenterProfileInlineDataQuery } from '../../types/GraphqlTypes';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import {
  emitRenterProfileChatBubbleAboutMeClickEvent,
  RENTER_PROFILE_CHAT_BUBBLE_ABOUT_ME_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/renterProfile';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { getFormattedAttributesList } from '../../utils/renterProfileUtils';
import { useAuth } from '@zg-rentals/auth-comet';

const MAX_SIZE_ATTRIBUTES_LIST = 3;

export default function RenterProfileChatBubble(): JSX.Element | null {
  const { isMobileView } = useDevices();
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const { changeMobileView } = useInboxRouterHelper();
  const { user } = useAuth();
  const { data } = useRenterProfileInlineDataQuery({
    variables: {
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
    },
    fetchPolicy: 'cache-only',
  });

  if (!data) {
    return null;
  }

  const { renterProfile, conversation } = data?.ZRM_getConversationById ?? {};
  const { senderName } = conversation[0] ?? {};
  const formattedAttributesList = getFormattedAttributesList(renterProfile?.attributes);

  if (!formattedAttributesList.length) {
    return null;
  }

  // If formattedAttributesList.length > 0, renterProfile & renterProfile.attributes exist
  const updatedAtMs = renterProfile?.attributes?.updatedAtMs;
  const formattedTimestamp = updatedAtMs
    ? `as of ${format(new Date(updatedAtMs ? +updatedAtMs : ''), 'MMM d, h:mm a')}`
    : '';

  const handleAboutMeClick = (): void => {
    if (isMobileView) {
      emitRenterProfileChatBubbleAboutMeClickEvent(user);
      return changeMobileView(InboxPageMobileView.RenterProfile);
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'row', width: '100%', marginBlock: 'default', marginRight: 'auto' }}>
      <ChatAvatar hasTimestampInMessage={false} senderFullName={senderName} type="user" />

      <Box css={{ ...chatBubbleStyles, ...defaultChatBubbleStyles, padding: 0 }}>
        <Box css={{ display: 'flex', alignItems: 'center', padding: 'default' }}>
          <Icon size="md" css={{ color: 'text.subtle', marginRight: 'default' }}>
            <IconUserFilled />
          </Icon>

          <Box>
            <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle' }}>
              About me
            </Paragraph>

            <Text asChild textStyle="body-xs">
              <time>{formattedTimestamp}</time>
            </Text>
          </Box>

          {isMobileView && (
            <IconButton
              data-eventid={RENTER_PROFILE_CHAT_BUBBLE_ABOUT_ME_CLICK_EVENT_TYPE_ID}
              emphasis="bare"
              icon={<IconChevronRightFilled />}
              onClick={handleAboutMeClick}
              shape="circle"
              size="sm"
              title="View renter profile"
              tone="neutral"
              css={{ marginLeft: 'default' }}
            />
          )}
        </Box>

        <Divider />

        <Box css={{ padding: 'default' }}>
          {formattedAttributesList.map((attribute, idx) => {
            if (idx < MAX_SIZE_ATTRIBUTES_LIST) {
              return (
                <div key={`attribute-${idx}`} data-test="renter-profile-inline-attribute">
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    {attribute.title}
                  </Paragraph>

                  <Paragraph textStyle="body-sm" css={{ marginBottom: 'tighter' }}>
                    {attribute.value}
                  </Paragraph>
                </div>
              );
            }
          })}

          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}>
            {isMobileView ? 'More...' : 'See more in Renter Profile...'}
          </Paragraph>
        </Box>
      </Box>
    </Box>
  );
}
